/**
 * uiHighlight – tiny standalone helper that scrolls an element into view
 * and pulses a soft ring around it for ~2 seconds. Used by:
 *   - "Show me where" buttons in ExplainPanel
 *   - "Open" / "Filter" recommendations in RecommendationsPanel
 *
 * Independent of DemoGuideOverlay so it works even when demo mode is off.
 */

const HIGHLIGHT_CLASS = 'ui-explain-highlight';
const HIGHLIGHT_DURATION_MS = 2200;

let styleInjected = false;

function ensureStyles(): void {
  if (styleInjected) return;
  if (typeof document === 'undefined') return;

  const style = document.createElement('style');
  style.setAttribute('data-ui-highlight', 'true');
  style.textContent = `
    @keyframes uiExplainPulse {
      0%   { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.55); outline-color: rgba(99, 102, 241, 0.95); }
      70%  { box-shadow: 0 0 0 14px rgba(99, 102, 241, 0); outline-color: rgba(99, 102, 241, 0.45); }
      100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);    outline-color: rgba(99, 102, 241, 0.95); }
    }
    .${HIGHLIGHT_CLASS} {
      position: relative;
      outline: 2px solid rgba(99, 102, 241, 0.9);
      outline-offset: 4px;
      border-radius: 6px;
      animation: uiExplainPulse 1.1s ease-out 2;
      transition: outline-color 200ms ease;
      z-index: 1;
    }
  `;
  document.head.appendChild(style);
  styleInjected = true;
}

export function highlightSelector(selector: string | undefined): boolean {
  if (!selector || typeof document === 'undefined') return false;
  ensureStyles();

  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return false;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.remove(HIGHLIGHT_CLASS);
  // Re-trigger animation on the next frame.
  requestAnimationFrame(() => {
    el.classList.add(HIGHLIGHT_CLASS);
    window.setTimeout(() => {
      el.classList.remove(HIGHLIGHT_CLASS);
    }, HIGHLIGHT_DURATION_MS);
  });

  return true;
}
